import { PageHeader } from '@ant-design/pro-components';
import { Button, Empty, List, Radio, Row, Space, Spin, Statistic } from 'antd';
import { Col } from 'antd';
import dayjs from 'dayjs';
import { useCompany } from '../../hooks/company';
import { useRequest } from 'ahooks';
import {
  BaseDepartmentDTO,
  companyRankController,
  HomeTopDTO,
  Long,
} from '../../api';
import { useMemo, useState } from 'react';
import { flatDepartment } from '../../utils';
import Top1 from '../../assets/jiangpai01.png';
import Top2 from '../../assets/jiangpai02.png';
import Top3 from '../../assets/jiangpai03.png';
import './rank.scss';

export default () => {
  // 获取中文月份的方法
  const {
    config: { companyRankConfig } = {},
    id: companyId,
    currentUser: { isSuperAdmin },
    hasPermission,
  } = useCompany();

  const defaultPermissionType = useMemo(() => {
    if (hasPermission('page:rank:big-department')) {
      return 'big-department';
    }
    if (hasPermission('page:rank:department')) {
      return 'department';
    }
    if (hasPermission('page:rank:person')) {
      return 'person';
    }
    return undefined;
  }, [hasPermission]);

  const [type, setType] = useState<
    'person' | 'department' | 'big-department' | undefined
  >(defaultPermissionType);
  const isDama = !isSuperAdmin && type === 'person';

  const {
    data,
    loading,
    refresh: refresh,
  } = useRequest(
    () => {
      return Promise.all(
        Object.entries(companyRankConfig?.companies || {}).map(
          ([rmsCompangId, rmsCompanyName]) => {
            return companyRankController
              .getHomeTop(companyId, rmsCompangId)
              .then((item) => ({
                homeTop: item.homeTop?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.userId}`,
                })),
                department: item.departments?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.id}`,
                })),
                users: item.users?.map((item) => ({
                  ...item,
                  rmsCompanyName,
                  rmsCompangId,
                  key: `${rmsCompangId}-${rmsCompanyName}-${item.id}`,
                })),
              }));
          },
        ),
      );
    },
    {
      refreshDeps: [companyRankConfig, companyId],
    },
  );

  const rankData = useMemo(() => {
    const homeTops = data?.map((item) => item.homeTop).flat();
    const departments = data
      ?.map((item) => item.department)
      .flat()
      .reduce((prev, cur) => {
        if (cur) {
          prev[cur.rmsCompangId] = [...(prev[cur.rmsCompangId] || []), cur];
        }
        return prev;
      }, {} as Record<Long, BaseDepartmentDTO[]>);

    switch (type) {
      case 'person':
        return homeTops
          ?.sort(
            (a, b) => Number(b?.brokerage || 0) - Number(a?.brokerage || 0),
          )
          .map((item) => {
            const department =
              item?.rmsCompangId && departments
                ? flatDepartment(
                    departments?.[item.rmsCompangId],
                    item.departmentId,
                  ).map((item) => item.name)
                : ['未知部门'];
            return {
              ...item,
              name: item?.userName,
              department: department.reverse(),
            };
          });
      case 'department':
        const values = Object.values(
          homeTops?.reduce((prev, cur) => {
            if (cur) {
              const key = `${cur.rmsCompangId}-${cur.departmentId}`;
              prev[key] = {
                ...cur,
                brokerage:
                  (Number(cur.brokerage) || 0) +
                  (Number(prev[key]?.brokerage) || 0),
                rmsCompanyId: cur.rmsCompangId,
                rmsCompanyName: cur.rmsCompanyName,
                key,
              };
            }
            return prev;
          }, {} as Record<Long, HomeTopDTO & { key: string; rmsCompanyId: Long; rmsCompanyName: string }>) ||
            {},
        );
        return values
          .sort((a, b) => Number(b.brokerage) - Number(a.brokerage))
          .map((item) => {
            const department =
              item?.rmsCompanyId && departments
                ? flatDepartment(
                    departments?.[item.rmsCompanyId],
                    item.departmentId,
                  ).map((item) => item.name)
                : ['未知部门'];
            return {
              ...item,
              name: department[0],
              department: department,
            };
          });
      case 'big-department':
        return data
          ?.map((compamyItem) => compamyItem.department)
          .flat()
          .filter((item) => !item?.parentId)
          .map((currentBigDepartment) => {
            if (!currentBigDepartment?.rmsCompangId) {
              throw new Error('rmsCompangId is required');
            }
            const currentHomeTop = homeTops?.filter(
              (homeTopsItem) =>
                homeTopsItem?.rmsCompangId ===
                currentBigDepartment.rmsCompangId,
            );

            // 找到当前大区所有的子部门
            const childrenDepartmentIds = (
              departments?.[currentBigDepartment.rmsCompangId] || []
            )
              .filter((item) => item.parentId === currentBigDepartment.id)
              .map((item) => item.id);

            // 找到属于这个大区的人的业绩
            const bigDepartmentBrokerage = currentHomeTop
              ?.filter(
                (homeHopItem) =>
                  homeHopItem?.departmentId === currentBigDepartment.id,
              )
              .reduce((prev, cur) => {
                return prev + Number(cur?.brokerage || 0);
              }, 0);
            // 找到属于大区子部门人的业绩
            const bigDepartmentChildrenBrokerage = currentHomeTop
              ?.filter((homeTopsItem) =>
                childrenDepartmentIds.includes(
                  homeTopsItem?.departmentId || '',
                ),
              )
              .reduce((prev, cur) => {
                return prev + Number(cur?.brokerage || 0);
              }, 0);

            const result: HomeTopDTO & {
              department: string[];
              name: string;
              key: string;
              rmsCompanyName: string;
            } = {
              ...currentBigDepartment,
              loanAmount: 0,
              loanCount: 0,
              userId: 0,
              userName: '',
              brokerage:
                (bigDepartmentBrokerage || 0) +
                (bigDepartmentChildrenBrokerage || 0),
              department: [currentBigDepartment.name],
              name: currentBigDepartment.name,
              key: `${currentBigDepartment.rmsCompangId}-${currentBigDepartment.id}`,
              rmsCompanyName: currentBigDepartment.rmsCompanyName,
            };
            return result;
          })
          .sort((a, b) => Number(b.brokerage) - Number(a.brokerage));
    }
  }, [data, type]);

  const top3 = useMemo(() => {
    if (!rankData?.length) {
      return [{ key: '2' }, { key: '1' }, { key: '3' }];
    }

    const value: Partial<(typeof rankData)[0]>[] = [
      { key: '2' },
      { key: '1' },
      { key: '3' },
    ];

    if (rankData?.length > 0) {
      value[1] = rankData[0];
    }
    if (rankData?.length > 1) {
      value[0] = rankData[1];
    }
    if (rankData?.length > 2) {
      value[2] = rankData[2];
    }

    return value;
  }, [rankData]);

  const noPermission = useMemo(() => {
    return (
      !hasPermission('page:rank:person') &&
      !hasPermission('page:rank:department') &&
      !hasPermission('page:rank:big-department')
    );
  }, [hasPermission]);

  const enable = useMemo(() => {
    const now = dayjs();
    const week = now.day() === 0 ? 7 : now.day();
    const currentDayTime = companyRankConfig?.time?.[week - 1];
    if (!currentDayTime) {
      return false;
    }
    if (!currentDayTime.enable) {
      return false;
    }
    const startDate = dayjs(
      `${now.format('YYYY-MM-DD')} ${currentDayTime.start}`,
    );
    // 此处原代码存在错误，应该使用 currentDayTime.end 而非 currentDayTime.enable
    const endDate = dayjs(`${now.format('YYYY-MM-DD')} ${currentDayTime.end}`);
    console.log(startDate, endDate, now);
    if (now.isBefore(startDate) || now.isAfter(endDate)) {
      return false;
    }
    return true;
  }, [companyRankConfig]);

  if (!enable || noPermission) {
    return (
      <Empty
        description={
          <Space>
            <span>榜单暂未开启，敬请期待</span>
            <Button
              type="link"
              onClick={() => {
                refresh();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      />
    );
  }

  if (loading) {
    return (
      <PageHeader
        className={`outer-box-rank ${
          type === 'person' ? 'person-mode' : 'depart-mode'
        }`}
      >
        <Spin spinning={loading} />
      </PageHeader>
    );
  }

  const getListDescription = (item: {
    rmsCompanyName?: string;
    department?: string[];
  }) => {
    switch (type) {
      case 'big-department':
        return `${item.rmsCompanyName}`;
      case 'department':
        return `${item.department?.slice(-1).reverse().join('/')}/${
          item.rmsCompanyName
        }`;
      case 'person':
        return `${item.department?.reverse().join('/')}/${item.rmsCompanyName}`;
    }
  };

  return (
    <PageHeader
      className={`outer-box-rank ${
        type === 'person' ? 'person-mode' : 'depart-mode'
      }`}
      extra={
        <Radio.Group value={type} onChange={(e) => setType(e.target.value)}>
          <Radio.Button
            disabled={!hasPermission('page:rank:person')}
            value="person"
          >
            个人
          </Radio.Button>
          <Radio.Button
            disabled={!hasPermission('page:rank:department')}
            value="department"
          >
            部门
          </Radio.Button>
          <Radio.Button
            disabled={!hasPermission('page:rank:big-department')}
            value="big-department"
          >
            大区
          </Radio.Button>
        </Radio.Group>
      }
    >
      <Row className="longhu-outer">
        <span className="month-show">{dayjs().format('MMMM')}</span>
        {top3.map((item, index) => (
          <Col
            span={8}
            key={item.key}
            style={{
              paddingTop: index !== 1 ? '50px' : '20px',
            }}
          >
            <Space direction="horizontal">
              <div className="longhu">
                {index === 0 && <img src={Top2} alt="第二名" />}
                {index === 1 && <img src={Top1} alt="第一名" />}
                {index === 2 && <img src={Top3} alt="第三名" />}
              </div>
              <Space direction="vertical">
                <Space>
                  <Statistic
                    value={item?.brokerage}
                    precision={2}
                    valueStyle={{
                      fontSize: 42,
                      color: 'red',
                      fontWeight: 'bold',
                    }}
                  />
                </Space>
                {type === 'person' && (
                  <Space>
                    <span className="top-item top-item-name">姓名：</span>
                    <span className="top-item top-item-inner">
                      {isDama && item.userName
                        ? item.userName.slice(0, -1) + '*'
                        : item.userName}
                    </span>
                  </Space>
                )}
                <Space>
                  <span className="top-item top-item-name">部门：</span>
                  <span className="top-item top-item-inner">
                    {item?.department?.join('/')}
                  </span>
                </Space>
                <Space>
                  <span className="top-item top-item-name">公司：</span>
                  <span className="top-item top-item-inner">
                    {item?.rmsCompanyName}
                  </span>
                </Space>
              </Space>
            </Space>
          </Col>
        ))}
      </Row>
      <Row>
        <Col span={24}>
          <List
            dataSource={rankData?.slice(3, 10).map((item) => ({
              ...item,
              brokerage: Number(item.brokerage || 0),
            }))}
            renderItem={(item, index) => {
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={<span>{index + 4}</span>}
                    title={
                      isDama && item.name
                        ? item.name.slice(0, -1) + '*'
                        : item.name
                    }
                    description={getListDescription(item)}
                  />
                  <Statistic value={item.brokerage} precision={2} />
                </List.Item>
              );
            }}
          />
        </Col>
      </Row>
    </PageHeader>
  );
};
